import type { FeatureFlags } from "../../config/featureFlags";
import type { QtoSourceId } from "./types";

export type { QtoSourceId };

/** What every QTO source provides regardless of archetype. */
type QtoSourceCommon = {
  /** What the UI shows on cards, drawer tiles and headers. */
  label: string;
  /**
   * Prefix for line-item `import_batch` strings (e.g. `"Togal Import - …"`). Deliberately separate
   * from `label`: these strings are already written into customers' workbooks, so they stay fixed
   * even when the display name is corrected.
   */
  importBatchLabel: string;
  featureFlagKey?: keyof FeatureFlags;
  /** Basename for the multipart CSV uploaded by the review submit pipeline. */
  csvMultipartBaseName: string;
  /** Partner logo, shown in place of an icon. */
  logo: string;
};

/** API-backed source: long-lived session, persisted across reloads, has a logout target. */
export type ApiQtoSource = QtoSourceCommon & { kind: "api" };

/** File-backed source: one-shot per upload, no auth, no logout target. */
export type FileQtoSource = QtoSourceCommon & { kind: "file" };

export type QtoSourceEntry = ApiQtoSource | FileQtoSource;

/** Minimal contract for an authenticated session: whether it is signed in, and how to sign out. */
export type ApiSourceSession = {
  isAuthenticated: boolean;
  logout: () => Promise<void>;
};

/**
 * Every QTO source, keyed by id. `satisfies Record<QtoSourceId, …>` is what makes this total: a new
 * id in the union is a compile error here rather than a lookup that misses at runtime.
 */
export const QTO_SOURCES = {
  togal: {
    kind: "api",
    label: "Togal.AI",
    importBatchLabel: "Togal",
    featureFlagKey: "last-mile-togal-integration",
    csvMultipartBaseName: "togal-qto-import",
    logo: "assets/partner_logos/togal.svg",
  },
  ato: {
    kind: "api",
    label: "Autodesk",
    importBatchLabel: "Autodesk Takeoff",
    featureFlagKey: "last-mile-autodesk-qto",
    csvMultipartBaseName: "ato-qto-import",
    logo: "assets/partner_logos/autodesk.svg",
  },
  ost: {
    kind: "file",
    label: "On-Screen Takeoff",
    importBatchLabel: "OnScreen Takeoff",
    featureFlagKey: "last-mile-ost-qto",
    csvMultipartBaseName: "ost-qto-import",
    logo: "assets/partner_logos/OST.svg",
  },
} as const satisfies Record<QtoSourceId, QtoSourceEntry>;

/** Display order, which is not the declaration order. */
export const QTO_SOURCE_ORDER: readonly QtoSourceId[] = ["togal", "ost", "ato"];

/** The API-archetype ids, narrowed so session tables keyed on them can be checked exhaustively. */
export type ApiQtoSourceId = {
  [K in QtoSourceId]: (typeof QTO_SOURCES)[K]["kind"] extends "api" ? K : never;
}[QtoSourceId];
